import React, { useState, useEffect } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Typography,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';
import api from '../config/api';

const EvaluationAlert = ({
    pendingCount = 0, // Cantidad de candidatos pendientes
    showDetailedInfo = false, // Mostrar información detallada
    onUsageCheckComplete = null, // Callback cuando se complete la verificación
    compact = false // Versión compacta de la alerta
}) => {
    const { t } = useTranslation();
    const { organization, authToken } = useStore();

    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    // Verificar límites de uso para assessments
    const checkUsageLimits = async () => {
        if (!organization || !authToken) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const { data } = await api.get(
                `/organizations/${organization}/usage/check`,
                {
                    params: { action: 'assessments' },
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            );

            if (data.response && data.response.status_response === "OK") {
                setUsageData(data.response);

                // Ejecutar callback si se proporciona
                if (onUsageCheckComplete) {
                    onUsageCheckComplete(data.response);
                }
            } else {
                console.error("Error al verificar límites de uso:", data);
                setError(t('evaluationAlert.limitsCouldNotBeVerified'));
                setUsageData({ canPerform: true }); // En caso de error, permitir uso
            }
        } catch (error) {
            console.error("Error al verificar límites de uso:", error);
            setError(t('evaluationAlert.limitsCouldNotBeVerified'));
            setUsageData({ canPerform: true }); // En caso de error, permitir uso
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        checkUsageLimits();
    }, [organization, authToken]);

    // Función para verificar si hay suficientes assessments para todos los pendientes
    const hasEnoughAssessmentsForAll = () => {
        if (!usageData || pendingCount === 0) return true;
        if (usageData.unlimited) return true;

        return usageData.remaining >= pendingCount;
    };

    // Función para obtener la cantidad de assessments disponibles
    const getAvailableAssessments = () => {
        if (!usageData) return 0;
        if (usageData.unlimited) return Infinity;

        const costPerAssessment = usageData.serviceCost || 2;
        return Math.floor(usageData.remaining / costPerAssessment);
    };

    // Función para obtener el porcentaje de uso
    const getUsagePercentage = () => {
        if (!usageData || usageData.unlimited) return 0;
        return Math.min((usageData.currentUsage / usageData.limit) * 100, 100);
    };

    // Función para obtener el color de la alerta según el nivel de uso
    const getAlertSeverity = () => {
        if (!usageData) return 'info';
        if (usageData.unlimited) return 'info';

        const percentage = getUsagePercentage();
        const hasEnoughForAll = hasEnoughAssessmentsForAll();

        if (!hasEnoughForAll && pendingCount > 0) return 'error';
        if (percentage >= 80) return 'warning';
        if (percentage >= 60) return 'info';
        return 'success';
    };

    // Si está cargando
    if (loading) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LinearProgress sx={{ flexGrow: 1, height: 6, borderRadius: 1 }} />
                    <Typography variant="body2">
                        {t('evaluationAlert.checkingAssessments')}
                    </Typography>
                </Box>
            </Alert>
        );
    }

    // Si hay error
    if (error) {
        return (
            <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{t('evaluationAlert.warning')}</AlertTitle>
                {error}.
            </Alert>
        );
    }

    // Si no hay datos de uso
    if (!usageData) {
        return null;
    }

    // Si la alerta fue descartada y hay suficientes usos, no mostrar
    if (dismissed && hasEnoughAssessmentsForAll()) {
        return null;
    }

    const severity = getAlertSeverity();
    const percentage = getUsagePercentage();
    const available = getAvailableAssessments();
    const hasEnoughForAll = hasEnoughAssessmentsForAll();

    // Mensaje principal de la alerta
    const getAlertMessage = () => {
        if (!hasEnoughForAll && pendingCount > 0) {
            return t('evaluationAlert.insufficientMessage', {
                available: available,
                needed: pendingCount
            });
        }

        if (usageData.unlimited) {
            return t('evaluationAlert.unlimitedMessage');
        }

        if (pendingCount > 0) {
            return t('evaluationAlert.availableAndPending', {
                available: available,
                pending: pendingCount
            });
        }

        return t('evaluationAlert.remainingMessage', {
            remaining: available,
            limit: usageData.limit
        });
    };

    // Versión compacta para mostrar en línea
    if (compact) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                    label={t('evaluationAlert.availableCompact', { count: available })}
                    size="small"
                    color={severity}
                    variant="outlined"
                />
                {pendingCount > 0 && (
                    <Chip
                        label={t('evaluationAlert.pendingCompact', { count: pendingCount })}
                        size="small"
                        color={hasEnoughForAll ? 'success' : 'error'}
                        variant="outlined"
                    />
                )}
            </Box>
        );
    }

    return (
        <Alert
            severity={severity}
            sx={{ mb: 2 }}
            action={
                hasEnoughForAll && !usageData.unlimited && (
                    <Tooltip title={t('evaluationAlert.dismissAlert')}>
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setDismissed(true)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                )
            }
        >
            <AlertTitle>
                {!hasEnoughForAll && pendingCount > 0 ?
                    t('evaluationAlert.insufficientAssessments') :
                    t('evaluationAlert.assessmentsStatus')
                }
            </AlertTitle>

            <Typography variant="body2" sx={{ mb: showDetailedInfo ? 2 : 0 }}>
                {getAlertMessage()}
            </Typography>

            {/* Información detallada */}
            {showDetailedInfo && (
                <Box sx={{ mt: 2 }}>
                    {!usageData.unlimited && (
                        <>
                            {/* Barra de progreso */}
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('evaluationAlert.currentUsage', {
                                            current: usageData.currentUsage,
                                            limit: usageData.limit === -1 ? '∞' : usageData.limit
                                        })}{' '}
                                        ({percentage.toFixed(1)}%)
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={percentage}
                                    sx={{
                                        height: 8,
                                        borderRadius: 1,
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor:
                                                severity === 'error' ? 'error.main' :
                                                    severity === 'warning' ? 'warning.main' :
                                                        severity === 'info' ? 'info.main' :
                                                            'success.main'
                                        }
                                    }}
                                />
                            </Box>

                            {/* Chips informativos */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label={t('evaluationAlert.usedChip', { count: usageData.currentUsage })}
                                    size="small"
                                    color={severity}
                                    variant="outlined"
                                />
                                <Chip
                                    label={t('evaluationAlert.availableChip', { count: available })}
                                    size="small"
                                    color={hasEnoughForAll ? 'success' : 'error'}
                                    variant="outlined"
                                />
                                {pendingCount > 0 && (
                                    <Chip
                                        label={t('evaluationAlert.pendingCandidatesChip', { count: pendingCount })}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </>
                    )}

                    {usageData.unlimited && (
                        <Chip
                            label={t('evaluationAlert.unlimitedChip')}
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    )}
                </Box>
            )}
        </Alert>
    );
};

export default EvaluationAlert;